import { useState } from "react";
import { Brand } from "../../components/Brand";
import { api } from "../../api";
import { sendPasswordReset } from "../../lib/firebaseAuth";
import { passwordChangeEnabled } from "../../lib/passwordChangeFlag";
import {
  MAX_PASSWORD_LENGTH,
  PASSWORD_HINT,
  PASSWORD_TOO_SHORT,
  passwordTooShort,
} from "../../lib/passwordPolicy";
import { reportSwallowed } from "../../lib/reportSwallowed";
import {
  passwordResetProblem,
  signupFailureText,
} from "../../lib/signupErrors";
import { useAuthStore } from "../../stores/authStore";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { takeSignInNotice } from "./signInNotice";

const RESET_ON_THE_WAY =
  "If that email has an account, a reset link is on its way.";

// Email+password login / signup (ES256 JWT from BE-light). The system starts EMPTY —
// new users create an account here (signup endpoint pending on BE-light; see §F).
export function Login() {
  const login = useAuthStore((s) => s.login);
  const signup = useAuthStore((s) => s.signup);
  const error = useAuthStore((s) => s.error);
  const signupFailure = useAuthStore((s) => s.signupFailure);
  const status = useAuthStore((s) => s.status);
  const [notice] = useState(() => takeSignInNotice());
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgMode, setOrgMode] = useState<"create" | "join">("create");
  // The STORE defaults to Advanced for an absent value, which looks like a contradiction and is
  // not: absent means "never chose", and an existing user who never chose must keep today's
  // behaviour.
  const [uiMode, setUiMode] = useState<"easy" | "advanced">("easy");
  const [joinCode, setJoinCode] = useState("");
  const [codeState, setCodeState] = useState<
    | { kind: "idle" }
    | { kind: "checking" }
    | { kind: "bad" }
    | { kind: "ok"; orgName?: string | null }
    | { kind: "unverified" }
    | { kind: "rejected" }
  >({ kind: "idle" });
  const busy = status === "authenticating" || codeState.kind === "checking";

  // Pre-flight the code so a typo is caught at the FIELD rather than after a failed submit. It's advisory:
  // the endpoint is unauthenticated and may not exist, so `null` means "couldn't check" and must never be
  // shown as "invalid". The real guarantee is server-side — signup validates the code before creating
  // anything (`Back-End-Light__signup-with-join-code`).
  const submit = async () => {
    if (mode === "login") {
      await login(email, password).catch((err) =>
        reportSwallowed("login", err),
      );
      return;
    }
    if (passwordTooShort(password)) {
      useAuthStore.setState({
        status: "idle",
        error: PASSWORD_TOO_SHORT,
        signupFailure: { kind: "weak-password", policy: PASSWORD_TOO_SHORT },
      });
      return;
    }
    if (orgMode === "create") {
      await signup(email, password, undefined, uiMode).catch((err) =>
        reportSwallowed("signup", err),
      );
      return;
    }
    const code = joinCode.trim();
    if (!code) return;
    setCodeState({ kind: "checking" });
    const check = await api.checkJoinCode(code);
    if (check && !check.valid) {
      // A definitive answer: stop BEFORE creating the account, which is the whole point of the check.
      setCodeState({ kind: "bad" });
      return;
    }
    // A `valid:true` is a LABEL, not permission (BE-Light): the code can be rotated between check and
    // submit, so it never gates the submit — signup is the authority and does its lookup inside the insert.
    setCodeState(
      check ? { kind: "ok", orgName: check.org_name } : { kind: "unverified" },
    );
    try {
      await signup(email, password, code, uiMode);
    } catch (err) {
      // The code field note is only for a join-code refusal. An email that is
      // already in use keeps the check's ok/unverified note.
      reportSwallowed("signup", err);
      if (useAuthStore.getState().signupFailure?.kind === "join-code")
        setCodeState({ kind: "rejected" });
    }
  };

  const switchToSignIn = () => {
    setMode("login");
    setPassword("");
    useAuthStore.setState({ error: null, signupFailure: null });
  };

  return (
    <div className="flex h-full items-center justify-center bg-canvas p-4">
      <form
        className="w-full max-w-sm space-y-3 rounded-lg border border-line bg-surface p-6 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="flex items-center gap-2">
          <Brand size="lg" />
        </div>

        {notice && (
          <p
            role="status"
            className="rounded border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-400"
          >
            {notice}
          </p>
        )}

        <h2 className="text-sm font-medium text-muted">
          {mode === "login" ? "Sign in" : "Create your account"}
        </h2>

        <input
          className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder-faint outline-none focus:border-muted"
          type="email"
          placeholder="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder-faint outline-none focus:border-muted"
          type="password"
          placeholder="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          maxLength={mode === "signup" ? MAX_PASSWORD_LENGTH : undefined}
          required
        />
        {mode === "signup" && (
          <p className="text-[10px] text-faint">{PASSWORD_HINT}</p>
        )}
        {mode === "login" && <ForgotPassword email={email} />}
        {mode === "signup" && (
          <div className="space-y-2">
            {/* Radios, not a switch: both options are named, so nobody has to infer what the off-state means. */}
            <div className="grid grid-cols-2 gap-2">
              {(["create", "join"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={orgMode === m}
                  onClick={() => {
                    setOrgMode(m);
                    setCodeState({ kind: "idle" });
                  }}
                  className={`rounded border px-2 py-1.5 text-xs ${
                    orgMode === m
                      ? "border-accent bg-accent/10 text-fg"
                      : "border-line text-muted hover:bg-hover"
                  }`}
                >
                  {m === "create" ? "Create organization" : "Join existing org"}
                </button>
              ))}
            </div>

            {/* Radios, not a switch: both options are named, so nobody has to infer what the off-state means — and "Advanced" is not the negation of "Easy". */}
            <div className="grid grid-cols-2 gap-2">
              {(["easy", "advanced"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={uiMode === m}
                  onClick={() => setUiMode(m)}
                  className={`rounded border px-2 py-1.5 text-xs ${
                    uiMode === m
                      ? "border-accent bg-accent/10 text-fg"
                      : "border-line text-muted hover:bg-hover"
                  }`}
                >
                  {m === "easy" ? "Easy Mode" : "Advanced Mode"}
                </button>
              ))}
            </div>

            {orgMode === "join" && (
              <div className="space-y-1">
                <input
                  className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm tracking-wider text-fg placeholder-faint outline-none focus:border-muted"
                  placeholder="join code"
                  aria-label="Organization join code"
                  autoComplete="off"
                  value={joinCode}
                  onChange={(e) => {
                    setJoinCode(e.target.value);
                    setCodeState({ kind: "idle" });
                  }}
                  required
                />
                {codeState.kind === "bad" && (
                  <p className="text-xs text-red-400">
                    That join code isn’t valid — nothing was created. Check it
                    with whoever invited you.
                  </p>
                )}
                {codeState.kind === "ok" && (
                  <p className="text-xs text-emerald-400">
                    You’ll join{" "}
                    {codeState.orgName
                      ? `“${codeState.orgName}”`
                      : "that organization"}
                    .
                  </p>
                )}
                {codeState.kind === "rejected" && (
                  <p className="text-xs text-red-400">
                    Sign-up was refused. This join code may have changed —
                    nothing was created.
                  </p>
                )}
                {codeState.kind === "unverified" && (
                  <p className="text-xs text-faint">
                    Couldn’t check the code from here — it’ll be verified when
                    you submit.
                  </p>
                )}
                <p className="text-[10px] text-faint">
                  Ask an admin of that organization for its join code.
                </p>
              </div>
            )}
          </div>
        )}

        {mode === "signup" && signupFailure?.kind === "email-in-use" && (
          <EmailInUse email={email} onSignIn={switchToSignIn} />
        )}
        {mode === "signup" &&
          signupFailure &&
          signupFailure.kind !== "email-in-use" &&
          signupFailure.kind !== "join-code" && (
            <p className="text-xs text-red-400">
              {signupFailureText(signupFailure)}
            </p>
          )}
        {mode === "login" && error && (
          <p className="text-xs text-red-400">{error}</p>
        )}

        <button
          disabled={busy}
          className="w-full rounded bg-accent px-3 py-2 text-sm font-medium text-canvas hover:bg-accent-hover disabled:opacity-60"
        >
          {busy
            ? "…"
            : mode === "login"
              ? "Sign in"
              : orgMode === "join"
                ? "Join & create account"
                : "Create account"}
        </button>

        <button
          type="button"
          className="w-full text-center text-xs text-muted hover:text-fg"
          onClick={() => {
            setMode((m) => (m === "login" ? "signup" : "login"));
            useAuthStore.setState({ error: null, signupFailure: null });
          }}
        >
          {mode === "login"
            ? "No account yet? Create one"
            : "Already have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}

function usePasswordReset(email: string) {
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const send = () => {
    setNote(null);
    setProblem(null);
    void sendPasswordReset(email.trim())
      .then(() => {
        setNote(RESET_ON_THE_WAY);
      })
      .catch((err: unknown) => {
        reportSwallowed("password reset", err);
        const problem = passwordResetProblem(err);
        if (problem) setProblem(problem);
        else setNote(RESET_ON_THE_WAY);
      });
  };
  return { note, problem, send };
}

function ForgotPassword({ email }: { email: string }) {
  const legacy = usePasswordReset(email);
  const [open, setOpen] = useState(false);
  const flagged = passwordChangeEnabled();
  return (
    <div className="space-y-1">
      <button
        type="button"
        className="text-xs text-muted hover:text-fg"
        onClick={flagged ? () => setOpen(true) : legacy.send}
      >
        Forgot password?
      </button>
      {!flagged && legacy.note && (
        <p className="text-xs text-muted">{legacy.note}</p>
      )}
      {!flagged && legacy.problem && (
        <p className="text-xs text-red-400">{legacy.problem}</p>
      )}
      {flagged && open && (
        <ResetPasswordDialog
          email={email}
          onClose={() => setOpen(false)}
          onBack={() => setOpen(false)}
          onSend={(address) => api.requestPasswordReset(address)}
        />
      )}
    </div>
  );
}

function EmailInUse({
  email,
  onSignIn,
}: {
  email: string;
  onSignIn: () => void;
}) {
  const legacy = usePasswordReset(email);
  const [open, setOpen] = useState(false);
  const flagged = passwordChangeEnabled();
  return (
    <div className="space-y-1">
      <p className="text-xs text-red-400">That email is already in use.</p>
      <p className="text-xs">
        <button
          type="button"
          className="text-muted hover:text-fg"
          onClick={onSignIn}
        >
          Sign in instead
        </button>
        <span className="text-muted"> · </span>
        <button
          type="button"
          className="text-muted hover:text-fg"
          onClick={flagged ? () => setOpen(true) : legacy.send}
        >
          Reset password
        </button>
      </p>
      {!flagged && legacy.note && (
        <p className="text-xs text-muted">{legacy.note}</p>
      )}
      {!flagged && legacy.problem && (
        <p className="text-xs text-red-400">{legacy.problem}</p>
      )}
      {flagged && open && (
        <ResetPasswordDialog
          email={email}
          onClose={() => setOpen(false)}
          onBack={() => setOpen(false)}
          onSend={(address) => api.requestPasswordReset(address)}
        />
      )}
    </div>
  );
}
